// frontend/src/pages/PayrollItems/components/NewItemMenu.tsx
// "New payroll item" dropdown with 4 create options.
// Self-contained: owns open state, outside-click, ESC-to-close.
// Parent decides what each option does (alert placeholder today, real flows later).

import React, { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  CircleDollarSign,
  Receipt,
  CalendarClock,
  MapPin,
} from "lucide-react";
import { BG_CARD, BORDER, TEXT_PRIMARY, TEXT_SECONDARY, BTN_TEAL_OUT } from "../styles";

interface NewItemMenuProps {
  onCreatePayType: () => void;
  onCreateDeduction: () => void;
  onCreateSchedule: () => void;
  onCreateLocation: () => void;
}

export default function NewItemMenu({
  onCreatePayType,
  onCreateDeduction,
  onCreateSchedule,
  onCreateLocation,
}: NewItemMenuProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(function () {
    if (!isOpen) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setIsOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return function () {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isOpen]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setIsOpen(!isOpen)} style={BTN_TEAL_OUT}>
        New payroll item <ChevronDown size={16} />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 46,
            background: BG_CARD,
            border: "0.5px solid " + BORDER,
            borderRadius: 10,
            boxShadow: "0 10px 30px rgba(20,40,40,0.14)",
            padding: 6,
            minWidth: 240,
            zIndex: 50,
          }}
        >
          <MenuItem
            icon={<CircleDollarSign size={15} style={{ color: TEXT_SECONDARY }} />}
            label="Pay type"
            onClick={() => { setIsOpen(false); onCreatePayType(); }}
          />
          <MenuItem
            icon={<Receipt size={15} style={{ color: TEXT_SECONDARY }} />}
            label="Deductions & contributions"
            onClick={() => { setIsOpen(false); onCreateDeduction(); }}
          />
          <MenuItem
            icon={<CalendarClock size={15} style={{ color: TEXT_SECONDARY }} />}
            label="Pay schedule"
            onClick={() => { setIsOpen(false); onCreateSchedule(); }}
          />
          <MenuItem
            icon={<MapPin size={15} style={{ color: TEXT_SECONDARY }} />}
            label="Work location"
            onClick={() => { setIsOpen(false); onCreateLocation(); }}
          />
        </div>
      )}
    </div>
  );
}

// ── Local sub-component: single menu row ────────────────────────────────
function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "10px 12px",
        borderRadius: 7,
        cursor: "pointer",
        fontSize: 13.5,
        color: TEXT_PRIMARY,
      }}
    >
      {icon}
      {label}
    </div>
  );
}